import { contentIndexUrl, normalizeUrlNavigation } from './urlQuery';
import type { ContentIndexQuery, UrlNavigationState } from './urlQuery';
import type { V3View } from '../types';

const CONTENT_INDEX_STORAGE_KEY = 'ci3-state-v1';
export const CONTENT_INDEX_RESET_KEY = 'ci3-account-switch-reset';

/** Called only after the selected account has been changed successfully. */
export function resetContentIndexForAccountSwitch(
  href: string,
  storage: Pick<Storage, 'removeItem'>,
  history: Pick<History, 'replaceState' | 'state'>,
  session?: Pick<Storage, 'setItem'>
): string | null {
  // The saved view/domain must not follow a user to the next account, even if
  // they are currently on a different page. A cross-page marker is only
  // needed when removal fails; normal deep links must remain usable.
  try {
    storage.removeItem(CONTENT_INDEX_STORAGE_KEY);
  } catch {
    try {
      session?.setItem(CONTENT_INDEX_RESET_KEY, '1');
    } catch {
      // The current Content Index URL also carries a one-shot reset flag.
    }
  }
  const url = new URL(href);
  if (url.pathname.replace(/\/+$/, '') === '/app/content-index') {
    // Clear only Content Index-owned query fields; preserve other params/hash.
    const cleanUrl = contentIndexUrl(href, { ciReset: '1' });
    try {
      history.replaceState(history.state, '', cleanUrl);
    } catch {
      // The caller can navigate directly when the History API is unavailable.
      return cleanUrl;
    }
  }
  return null;
}

/** Reset only after the server confirms a change to a different account. */
export async function switchAccountWithContentIndexReset(
  switchAccount: () => Promise<unknown>,
  customerChanged: boolean,
  href: string,
  storage: Pick<Storage, 'removeItem'>,
  history: Pick<History, 'replaceState' | 'state'>,
  session?: Pick<Storage, 'setItem'>
): Promise<string | null> {
  await switchAccount();
  return customerChanged ? resetContentIndexForAccountSwitch(href, storage, history, session) : null;
}

/** Keep reset provenance until the old persisted state is safely overwritten. */
export function keepContentIndexReset(pendingReset: boolean, persisted: boolean): boolean {
  return pendingReset && !persisted;
}

/** After a successful switch, navigation must happen even if optional cleanup fails. */
export function finishAccountSwitch(cleanup: () => void, navigate: () => void): void {
  try {
    cleanup();
  } finally {
    navigate();
  }
}

/** Choose navigation before any customer-scoped reads start. */
export function initialContentIndexNavigation(
  resetNavigation: boolean,
  query: ContentIndexQuery,
  saved: { view?: V3View; domain?: string }
): { view: V3View; domain: string; viewArgs: UrlNavigationState | null } {
  if (resetNavigation) return { view: 'today', domain: 'all', viewArgs: null };
  const views = ['today', 'groups', 'urls', 'monitor', 'activity', 'config'];
  const view = query.view && views.includes(query.view) ? (query.view as V3View) : saved.view || 'today';
  const viewArgs = normalizeUrlNavigation(query);
  return {
    view,
    domain: query.domain || saved.domain || 'all',
    viewArgs: Object.values(viewArgs).some(Boolean) ? viewArgs : null,
  };
}
